/**
 * Single QueryClient with localStorage persistence — survives page
 * reloads so users don't see skeletons on hard refresh of a hot route.
 *
 * Default options mirror the Next.js project (5-minute staleTime, no
 * aggressive refetch on focus) so behaviour is identical across the
 * coexistence window.
 */

import { QueryClient } from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min — match Next.js project defaults.
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

export const persister =
  typeof window === "undefined"
    ? undefined
    : createSyncStoragePersister({
        storage: window.localStorage,
        key: "wr-query-cache",
        // Bump when the API contract changes in a breaking way; the
        // persister will discard older caches on first read.
        serialize: (data) => JSON.stringify({ v: 1, ...data }),
        deserialize: (raw) => {
          try {
            const obj = JSON.parse(raw) as { v?: number };
            if (obj.v !== 1) return undefined as never;
            return obj as never;
          } catch {
            return undefined as never;
          }
        },
      });
